import React, { useState, useEffect } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { ArrowLeftIcon, DocumentArrowDownIcon, PlayIcon, SpeakerWaveIcon, DocumentIcon, EyeIcon } from '@heroicons/react/24/outline';
import { lecturesAPI } from '../../services/lectureService';
// import { lecturesAPI } from '../../services/api';

const LectureView = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [lecture, setLecture] = useState(location.state?.lecture || null);
  const [loading, setLoading] = useState(!location.state?.lecture);

  const BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3005';

  const getFileUrl = (file) => {
    if (!file) return '';
    if (file.startsWith('http')) return file;
    // Remove leading slash if present
    const cleanFile = file.startsWith('/') ? file.substring(1) : file;
    return `${BASE_URL}/${cleanFile}`;
  };

  useEffect(() => {
    const fetchLecture = async () => {
      if (!id) { 
        setLoading(false);
        return;
      }
      try {
        if (!lecture) setLoading(true);
        const response = await lecturesAPI.getLectureById(id);
        if (response?.data?.lecture) {
          setLecture(response.data.lecture);
        } else if (response?.data && response.data.id) {
          setLecture(response.data);
        } else {
          console.error('No lecture data found');
        }
      } catch (err) {
        console.error('Error fetching lecture:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchLecture();
  }, [id]);

  const getContentType = (contentTypeStr) => {
    if (!contentTypeStr) return 'unknown';
    return contentTypeStr.toLowerCase().split('/')[0];
  };

  const handleDownload = () => {
    const fileUrl = getFileUrl(lecture?.lectureFile || lecture?.content);
    if (fileUrl) {
      const link = document.createElement('a');
      link.href = fileUrl;
      link.download = lecture.title || 'download';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const handleOpenInNewTab = () => {
    const fileUrl = getFileUrl(lecture?.lectureFile || lecture?.content);
    if (fileUrl) {
      window.open(fileUrl, '_blank');
    }
  };

  const getFileExtension = (contentType) => {
    const type = (contentType || '').toLowerCase();
    if (type.includes('pdf')) return 'pdf';
    if (type.includes('powerpoint') || type.includes('presentation') || type.includes('ppt')) return 'ppt';
    if (type.includes('word') || type.includes('.document') || type.includes('wordprocessingml')) return 'word';
    if (type.includes('excel') || type.includes('spreadsheet')) return 'excel';
    return 'document';
  };

  const handleViewFile = () => {
    const fileUrl = getFileUrl(lecture?.lectureFile || lecture?.content);
    if (!fileUrl) return;

    const fileType = getFileExtension(lecture.contentType);
    const fileExtensions = {
      pdf: 'pdf',
      ppt: 'pptx',
      word: 'docx',
      excel: 'xlsx',
      document: 'pdf',
    };

    const ext = fileExtensions[fileType] || fileType;
    const fileName = `${lecture.title || 'lecture'}.${ext}`;

    const link = document.createElement('a');
    link.href = fileUrl;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const renderContent = () => {
    const fileUrl = getFileUrl(lecture?.lectureFile || lecture?.content);

    if (!lecture || !fileUrl) {
      return (
        <div className="lecture-view-document-container text-center py-12">
          <p className="text-gray-500">No content available</p>
        </div>
      );
    }

    const contentType = getContentType(lecture.contentType);
    const type = lecture.contentType?.toLowerCase() || '';

    switch (contentType) {
      case 'video':
        return (
          <div className="lecture-view-video-container">
            <video controls width="100%" style={{ height: '65vh' }} key={fileUrl} crossOrigin="anonymous">
              <source src={fileUrl} type={lecture.contentType} />
              Your browser does not support the video tag.
            </video>

          </div>
        );

      case 'audio':
        return (
          <div className="lecture-view-audio-container">
            <audio controls style={{ width: '100%', maxWidth: '600px' }} key={fileUrl} crossOrigin="anonymous">
              <source src={fileUrl} type={lecture.contentType} />
              Your browser does not support the audio tag.
            </audio>

          </div>
        );

      case 'application':
      case 'text':
      default:
        if (type.includes('pdf')) {
          return (
            <div className="lecture-view-document-container">
              <div className="mb-4">
                <button onClick={handleViewFile} className="lecture-view-view-btn flex items-center gap-2">
                  <EyeIcon className="w-5 h-5" />
                  View file in PDF format
                </button>
              </div>
              <iframe 
                src={fileUrl} 
                style={{ width: '100%', height: '70vh', border: 'none' }}
                title="PDF Viewer"
                onError={() => console.error('Failed to load PDF')}
              />
            </div>
          );
        }
        if (type.includes('powerpoint') || type.includes('presentation') || type.includes('ppt')) {
          return (
            <div className="lecture-view-document-container">
              <div className="mb-4">
                <button onClick={handleViewFile} className="lecture-view-view-btn flex items-center gap-2">
                  <EyeIcon className="w-5 h-5" />
                  View in PowerPoint Format
                </button>
              </div>
            </div>
          );
        }
        if (type.includes('word') || type.includes('.document') || type.includes('wordprocessingml')) {
          return (
            <div className="lecture-view-document-container">
              <div className="mb-4">
                <button onClick={handleViewFile} className="lecture-view-view-btn flex items-center gap-2">
                  <EyeIcon className="w-5 h-5" />
                  View in Word Format
                </button>
              </div>
            </div>
          );
        }
        if (type.includes('excel') || type.includes('spreadsheet') || type.includes('sheet')) {
          return (
            <div className="lecture-view-document-container">
              <div className="mb-4">
                <button onClick={handleViewFile} className="lecture-view-view-btn flex items-center gap-2">
                  <EyeIcon className="w-5 h-5" />
                  View in Excel Format
                </button>
              </div>
            </div>
          );
        }
        if (type.includes('text')) {
          return (
            <div className="lecture-view-document-container lecture-view-text-full-width">
              <iframe 
                src={fileUrl}
                style={{ width: '100%', height: '70vh', border: 'none' }}
                title="Text Viewer"
                onError={() => console.error('Failed to load text file')}
              />
            </div>
          );
        }
        return (
          <div className="lecture-view-document-container">
            <div className="lecture-view-document-info">
              <DocumentArrowDownIcon className="lecture-view-document-icon" />
              <p className="lecture-view-document-type">
                {lecture.contentType || 'Document File'}
              </p>
              <button onClick={handleOpenInNewTab} className="lecture-view-open-btn">
                Open in New Tab
              </button>
              <button onClick={handleDownload} className="lecture-view-download-btn">
                Download File
              </button>
            </div>
          </div>
        );
    }
  };

  if (loading) {
    return (
      <div className="lecture-view-container">
        <div className="lecture-view-loading">
          <p>Loading lecture...</p>
        </div>
      </div>
    );
  }


  return (
    <div style={{ height: 'calc(100vh - 64px)' }} className="flex flex-col overflow-hidden p-4">
      <div className="flex justify-between items-start w-full">

        {/* LEFT — Back Button */}
        <button
          onClick={() => navigate('/lectures')}
          className="lecture-view-back-btn flex items-center gap-2"
        >
          <ArrowLeftIcon className="lecture-view-icon" />
          Back to Lectures
        </button>

        {/* RIGHT — Title + Description */}
        <div className="text-right max-w-[70%]">
          <h1 className="lecture-view-title text-xxxl font-semibold">{lecture?.title}</h1>
          {/* <p className="lecture-view-description text-gray-600">{lecture?.description}</p> */}
        </div>

      </div>

      <div className="lecture-view-content">
        {renderContent()}
      </div>
    </div>
  );
};

export default LectureView;
